import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { Country } from '~/Api/Country';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { ErrorResponse, LtMaxPrice as ApiLtMaxPrice, Product, Update } from '~/Api/User/ProductGroup/LtMaxPrice';
import { handleError } from '~/ErrorHandler';
import { logGoogleTagManagerGroupProductsRemove } from '~/GoogleTagManager';
import { removeFromGroup } from '~/GroupProduct';
import {
	fetchReferenceBasketVersions,
	Module,
	pruneReferenceBasketVersions,
	selectReferenceBasketVersions,
	State as PriceReferencingState,
} from '~/PriceReferencing';
import { PricedProduct, Product as GeneralProduct } from '~/Product';
import { pruneDatasets } from '~/ProductDataset';
import { pruneAllPrices } from '~/ProductPrice';
import { ThunkDispatch } from '~/Store';

import { Action } from './Action';
import {
	commitLtMaxPriceUpdate,
	fetchLtMaxPrice,
	handleCommitError,
	handleLtMaxPriceUpdateResponse,
	pruneLtMaxPrice,
	scheduleLtMaxPriceUpdate,
} from './actions';
import LtMaxPriceContent from './LtMaxPrice';
import { selectLtMaxPrice, State as LtMaxPriceState } from './reducer';

interface BasketProduct {
	country: Country;
	product: Product;
	usedForCalculation: boolean;
}
interface BasketOptionalProduct {
	country: Country;
	product: Product | null;
	usedForCalculation: boolean;
}

const containsCountry = (countries: Country[], code: string): boolean =>
	countries.find((country) => country.alpha2 === code) !== undefined;

const compareReferences = (
	reference: BasketProduct | BasketOptionalProduct,
	otherReference: BasketProduct | BasketOptionalProduct
): number => {
	if (reference.product !== null && otherReference.product === null) {
		return -1;
	}
	if (reference.product === null && otherReference.product !== null) {
		return 1;
	}
	const comparison = reference.country.alpha2.localeCompare(otherReference.country.alpha2);
	if (comparison !== 0) {
		return comparison;
	}
	if (reference.product !== null && otherReference.product !== null) {
		return reference.product.product.brandName.localeCompare(otherReference.product.product.brandName);
	}
	return 0;
};

const groupProducts = memoizeOne((ltMaxPrice: ApiLtMaxPrice | null, versions: ReferenceBasketVersion[] | null) => {
	const version =
		versions !== null && ltMaxPrice !== null
			? versions.find((version) => version.id === ltMaxPrice.reference.referenceBasketVersion) ?? null
			: null;
	const referenceCountries = version !== null ? version.referenceCountries : [];
	const products = ltMaxPrice !== null && version !== null ? ltMaxPrice.products : [];
	const nonsetCountries = new Set(referenceCountries?.map((country) => country.alpha2));
	const usedForCalculationIds = new Set(
		ltMaxPrice?.finalCalculation?.gesvisPrice?.fromProducts?.map((product) => product.id) ?? []
	);

	const lithuanianProducts: BasketOptionalProduct[] = [];
	const referencedProducts: BasketOptionalProduct[] = [];
	const otherProducts: BasketProduct[] = [];

	products.forEach((product) => {
		const { country } = product.product.dataset.source;
		if (country.alpha2 === 'LT') {
			lithuanianProducts.push({
				country,
				product,
				usedForCalculation: usedForCalculationIds.has(product.product.id),
			});
			return;
		}
		if (containsCountry(referenceCountries ?? [], country.alpha2)) {
			referencedProducts.push({
				country,
				product,
				usedForCalculation: usedForCalculationIds.has(product.product.id),
			});
			nonsetCountries.delete(country.alpha2);
			return;
		}
		otherProducts.push({
			country,
			product,
			usedForCalculation: usedForCalculationIds.has(product.product.id),
		});
	});

	if (lithuanianProducts.length === 0) {
		lithuanianProducts.push({
			country: new Country('LT', 'Lithuania'),
			product: null,
			usedForCalculation: false,
		});
	}

	referenceCountries?.forEach((country) => {
		if (nonsetCountries.has(country.alpha2)) {
			referencedProducts.push({
				country,
				product: null,
				usedForCalculation: false,
			});
		}
	});

	lithuanianProducts.sort(compareReferences);
	referencedProducts.sort(compareReferences);
	otherProducts.sort(compareReferences);

	const basketProducts = [...lithuanianProducts, ...referencedProducts, ...otherProducts].filter(
		(product) => product.product !== null
	) as BasketProduct[];
	const pricedProducts = basketProducts.map(
		(product) => new PricedProduct(product.product, product.product.product, null)
	);

	return {
		lithuanianProducts,
		otherProducts,
		pricedProducts,
		referenceBasketVersion: version,
		referencedProducts,
	};
});

interface State {
	ltMaxPrice: LtMaxPriceState;
	priceReferencing: PriceReferencingState;
}

const mapStateToProps = (state: State, { group }: { group: UserProductGroup }) => {
	const ltMaxPriceState = selectLtMaxPrice(state.ltMaxPrice, group.id);
	const ltMaxPrice = ltMaxPriceState?.ltMaxPrice ?? null;
	const versions: ReferenceBasketVersion[] | null = selectReferenceBasketVersions(
		state.priceReferencing,
		Module.ltMaxPrice
	);

	return {
		...groupProducts(ltMaxPrice, versions),
		ltMaxPrice,
		isFetching: Boolean(ltMaxPriceState?.isFetching),
		validationResult: null,
		versions,
	};
};

const actions = {
	onFetch: (group: UserProductGroup) => async (dispatch: ThunkDispatch<AnyAction>) => {
		dispatch(fetchReferenceBasketVersions(Module.ltMaxPrice));

		try {
			await dispatch(commitLtMaxPriceUpdate(group.id));
		} catch (error) {
			dispatch(handleCommitError(error));
		}

		try {
			await dispatch(fetchLtMaxPrice(group.id));
		} catch (error) {
			if (!(error instanceof ErrorResponse)) {
				dispatch(handleError(error));
				return;
			}
			error.handleProductGroupNotFound();
			dispatch(handleError(error));
		}
	},
	onPrune: (group: UserProductGroup) => async (dispatch: ThunkDispatch<AnyAction>) => {
		try {
			await dispatch(commitLtMaxPriceUpdate(group.id));
		} catch (error) {
			dispatch(handleCommitError(error));
		}
		dispatch(pruneLtMaxPrice(group.id));
		dispatch(pruneReferenceBasketVersions());
		dispatch(pruneAllPrices());
		dispatch(pruneDatasets());
	},
	onSave: (group: UserProductGroup) => async (dispatch: ThunkDispatch<AnyAction>) => {
		try {
			await dispatch(commitLtMaxPriceUpdate(group.id));
		} catch (error) {
			dispatch(handleLtMaxPriceUpdateResponse(error, group.id));
		}
	},
	onUpdate: (group: UserProductGroup, update: Update) => async (dispatch: ThunkDispatch<AnyAction>) => {
		try {
			await dispatch(scheduleLtMaxPriceUpdate(group.id, update));
		} catch (error) {
			dispatch(handleLtMaxPriceUpdateResponse(error, group.id));
		}
	},
	onDeleteItems:
		(group: UserProductGroup, products: GeneralProduct[], onSuccess: () => void) =>
		async (dispatch: ThunkDispatch<AnyAction>) => {
			try {
				await dispatch(removeFromGroup(group.id, products));
				logGoogleTagManagerGroupProductsRemove(group.id, products);
				if (onSuccess !== undefined) {
					onSuccess();
				}
			} catch (error) {
				dispatch(handleError(error));
			}
		},
};

export default connect(mapStateToProps, actions)(LtMaxPriceContent);
